import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Trash2, X } from 'lucide-react';

import styles from './EntryDialog.module.scss';

/**
 * The dialog for adding or editing one portfolio entry. ×, Esc and the
 * backdrop cancel, and ask first when there are unsaved changes. Delete sits
 * in the footer and asks for an inline confirm.
 */
const EntryDialog = ({
  show,
  title,
  subtitle,
  dirty,
  canSave,
  saving,
  saveLabel,
  error,
  onSave,
  onClose,
  onDelete,
  deleteLabel,
  deletePrompt,
  crossLink,
  children,
}) => {
  // 'delete' or 'discard' while the footer is asking to confirm.
  const [confirming, setConfirming] = useState(null);
  // Runs once the person agrees to discard, e.g. opening another dialog.
  const [afterDiscard, setAfterDiscard] = useState(null);

  useEffect(() => {
    if (show) {
      setConfirming(null);
      setAfterDiscard(null);
    }
  }, [show]);

  const requestClose = (then = null) => {
    if (saving) {
      return;
    }
    if (dirty) {
      setAfterDiscard(() => then);
      setConfirming('discard');
      return;
    }
    onClose();
    if (then) {
      then();
    }
  };

  const discard = () => {
    onClose();
    if (afterDiscard) {
      afterDiscard();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (canSave && !saving) {
      onSave();
    }
  };

  const renderFooter = () => {
    if (confirming === 'delete') {
      return (
        <div className={`${styles.footer} ${styles.footerDanger}`}>
          <span className={styles.confirmText}>{deletePrompt}</span>
          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setConfirming(null)}
            >
              Keep it
            </button>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={onDelete}
            >
              {deleteLabel}
            </button>
          </div>
        </div>
      );
    }

    if (confirming === 'discard') {
      return (
        <div className={`${styles.footer} ${styles.footerWarning}`}>
          <span className={styles.confirmText}>Discard your changes?</span>
          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setConfirming(null)}
            >
              Keep editing
            </button>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={discard}
            >
              Discard
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.footer}>
        <div>
          {onDelete && (
            <button
              type="button"
              className={styles.deleteBtn}
              disabled={saving}
              onClick={() => setConfirming('delete')}
            >
              <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
              Delete
            </button>
          )}
        </div>
        <div className={styles.footerActions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={saving}
            onClick={() => requestClose()}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={!canSave || !!saving}
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={() => requestClose()}
      dialogClassName={styles.dialog}
      aria-labelledby="entry-dialog-title"
    >
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <header className={styles.header}>
          <div className={styles.headerText}>
            <h2 id="entry-dialog-title" className={styles.title}>
              {title}
            </h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => requestClose()}
            aria-label="Close without saving"
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </header>
        <div className={styles.body}>
          {children}
          {crossLink && (
            <div className={styles.crossLink}>
              {crossLink.icon}
              <span>
                {crossLink.prompt}{' '}
                <button
                  type="button"
                  className={styles.crossLinkBtn}
                  onClick={() => requestClose(crossLink.onClick)}
                >
                  {crossLink.label}
                </button>
              </span>
            </div>
          )}
          {error && (
            <p className={styles.formError} role="alert">
              {error}
            </p>
          )}
        </div>
        {renderFooter()}
      </form>
    </Modal>
  );
};

EntryDialog.propTypes = {
  show: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  dirty: PropTypes.bool,
  canSave: PropTypes.bool,
  saving: PropTypes.bool,
  saveLabel: PropTypes.string,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  deleteLabel: PropTypes.string,
  deletePrompt: PropTypes.string,
  crossLink: PropTypes.shape({
    icon: PropTypes.node,
    prompt: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  children: PropTypes.node,
};

EntryDialog.defaultProps = {
  saveLabel: 'Save changes',
  deleteLabel: 'Delete',
};

export default EntryDialog;
